import React, { useMemo, useState } from 'react';
import {
	addDays,
	addMonths,
	endOfMonth,
	endOfWeek,
	format,
	isSameDay,
	isSameMonth,
	startOfMonth,
	startOfWeek,
} from 'date-fns';

const API_BASE = 'https://api.insightos.co.uk';

const checklistSections = [
	{
		key: 'daily',
		title: 'Daily / Per Event',
		description: 'Track operational security and incident response actions every day or whenever events occur.',
		notePlaceholder: 'Link tickets, dashboards, or evidence collected today...',
		tasks: [
			'Review security_alerts, login_attempts, and user_sessions for anomalies; document findings.',
			'Investigate and resolve IP auto-ban entries; escalate threats through the IRP workflow.',
			'Log customer-facing incidents (availability, security, privacy) in the incident register within 24 hours.',
			'Capture availability outage evidence (monitoring screenshots, RCAs, rollback logs).',
			'Document security breach follow-up: SIEM alerts, IRP ticket, MFA resets, user notifications.',
			'Record privacy incident handling: support ticket, fix confirmation, ICO/customer communications.',
			'Attach data-loss recovery proof: restoration logs, hash checks, post-mortem summary.',
			'Note compliance deviations (e.g., vendor missteps) and containment/policy updates.',
			'Track service degradations (AI assistant throttling, etc.) and mitigation communications.',
			'Log malware/ransomware attempts with ClamAV evidence and patch confirmations.',
			'Capture third-party failures (Stripe webhooks, etc.) and compensating actions.',
			'Investigate insider misuse cases and document HR outcomes/access revocations.',
			'Record critical bug releases exposing data along with fix timeline and DPIA updates.',
		],
	},
	{
		key: 'weekly',
		title: 'Weekly',
		description: 'Executed every Monday or on the first available business day.',
		notePlaceholder: 'Patch summaries, npm audit output, backup verification links...',
		tasks: [
			'Apply Ubuntu/Plesk patches and export /var/log/plesk/patch-report.log.',
			'Run npm audit --production (or equivalent) and raise remediation tickets for highs/criticals.',
			'Verify nightly IONOS backup completion in the backup console.',
			'Capture security dashboard screenshots/PDFs for audit evidence.',
		],
	},
	{
		key: 'monthly',
		title: 'Monthly',
		description: 'Complete on the first calendar day each month.',
		notePlaceholder: 'Backup test hashes, privileged sign-offs, vendor updates...',
		tasks: [
			'Test backup restoration on staging; log hash validation and results in security_alerts (DEFCON=INFO).',
			'Review privileged access audit logs (admin/manager) and record approvals.',
			'Update remediation status for open vulnerabilities/exceptions.',
			'Rotate temporary credentials (support accounts, limited-use API keys).',
			'Update vendor register with new SaaS/third parties and confirm DPAs.',
		],
	},
	{
		key: 'quarterly',
		title: 'Quarterly',
		description: 'Schedule for the first day of each calendar quarter.',
		notePlaceholder: 'Tabletop agendas, isolation tests, DPIA/RoPA notes...',
		tasks: [
			'Execute Incident Response tabletop exercise; keep agenda, attendees, outcomes, action items.',
			'Retest forced logout & tenant isolation runbooks; attach console logs/remediation notes.',
			'Review DPIA and RoPA for processing changes; log “no change” statements when applicable.',
			'Conduct AI output/bias review with HR leads per DPIA mitigation plan.',
			'Perform access review of admin/manager accounts and document approvals/revocations.',
		],
	},
	{
		key: 'semiAnnual',
		title: 'Semi-Annual',
		description: 'Plan for January 1 and July 1 (or closest business day).',
		notePlaceholder: 'DR validation notes, penetration test reports...',
		tasks: [
			'Validate disaster recovery playbooks via rebuild or simulated failover.',
			'Re-run penetration tests or external vulnerability scans after major platform changes.',
		],
	},
	{
		key: 'annual',
		title: 'Annual',
		description: 'Run at the start of the fiscal/compliance year.',
		notePlaceholder: 'Key rotation confirmation, ISO updates, vendor attestations...',
		tasks: [
			'Rotate all production encryption keys (AES_KEY, deterministic hash key, JWT signing keys).',
			'Perform ISO/IEC 27001 control review; update Compliance Pack documents with revision dates.',
			'Reconfirm GDPR Article 30 (RoPA) and DPIA accuracy after yearly review.',
			'Ensure employee security awareness training is completed; archive certificates.',
			'Renew vendor security assessments (IONOS, Stripe, OpenAI, email providers) and file attestations.',
		],
	},
	{
		key: 'triggered',
		title: 'Trigger-Based / As Needed',
		description: 'Use whenever new releases, staffing changes, or major incidents occur.',
		notePlaceholder: 'Describe trigger, actions taken, evidence stored...',
		tasks: [
			'After major feature releases: update DPIA, RoPA, schema classification; revalidate encryption mappings.',
			'After staffing changes: revoke access, rotate credentials, log updated access reviews.',
			'After Critical/High incidents: run full IRP cycle, notify ICO/customers, update playbooks.',
			'When new analytics/telemetry launches: confirm GDPR purpose, retention, tenant segregation documentation.',
		],
	},
];

const calendarBadgeStyles = {
	weekly: 'bg-sky-100 text-sky-700',
	monthly: 'bg-amber-100 text-amber-700',
	quarterly: 'bg-emerald-100 text-emerald-700',
	semi: 'bg-purple-100 text-purple-700',
	annual: 'bg-rose-100 text-rose-700',
};

export default function ComplianceChecklist() {
	const [completedMap, setCompletedMap] = useState({});
	const [notes, setNotes] = useState({});
	const [currentMonth, setCurrentMonth] = useState(new Date());
	const [selectedDate, setSelectedDate] = useState('');
	const [reportContent, setReportContent] = useState([]);
	const [loadingReport, setLoadingReport] = useState(false);
	const [reportError, setReportError] = useState('');

	const monthMatrix = useMemo(() => {
		const start = startOfWeek(startOfMonth(currentMonth), { weekStartsOn: 0 });
		const end = endOfWeek(endOfMonth(currentMonth), { weekStartsOn: 0 });
		const weeks = [];
		let day = start;

		while (day <= end) {
			const week = Array.from({ length: 7 }).map((_, idx) => {
				const date = addDays(day, idx);
				return date;
			});
			weeks.push(week);
			day = addDays(week[6], 1);
		}

		return weeks;
	}, [currentMonth]);

	const toggleTask = (sectionKey, taskIndex) => {
		setCompletedMap((prev) => {
			const current = new Set(prev[sectionKey] || []);
			if (current.has(taskIndex)) {
				current.delete(taskIndex);
			} else {
				current.add(taskIndex);
			}
			return { ...prev, [sectionKey]: Array.from(current) };
		});
	};

	const handleNoteChange = (sectionKey, value) => {
		setNotes((prev) => ({ ...prev, [sectionKey]: value }));
	};

	const changeMonth = (offset) => {
		setCurrentMonth((prev) => addMonths(prev, offset));
	};

	const applyAllChecked = (enabled) => {
		if (!enabled) {
			setCompletedMap({});
			return;
		}
		const next = {};
		checklistSections.forEach((section) => {
			next[section.key] = section.tasks.map((_, idx) => idx);
		});
		setCompletedMap(next);
	};

	const fetchReportForDate = async (dateStr) => {
		if (!dateStr) {
			setReportError('Please select a date.');
			return;
		}
		setReportError('');
		setLoadingReport(true);
		setReportContent([]);
		try {
			const resp = await fetch(`${API_BASE}/api/securityreports/${encodeURIComponent(dateStr)}`, {
				credentials: 'include',
			});
			const data = await resp.json();
			if (!resp.ok) {
				throw new Error(data?.message || 'Failed to fetch security reports.');
			}
			const rows = Array.isArray(data?.data) ? data.data : [];
			if (rows.length > 0) {
				applyAllChecked(true);
				setReportContent(rows.map((r) => r.content ?? r.Content ?? JSON.stringify(r)));
			} else {
				applyAllChecked(false);
				setReportContent([]);
				setReportError('No reports found for that date.');
			}
		} catch (err) {
			applyAllChecked(false);
			setReportContent([]);
			setReportError(err?.message || 'Unable to load reports.');
		} finally {
			setLoadingReport(false);
		}
	};

	const getDayBadges = (date) => {
		const badges = [];
		if (date.getDay() === 1) badges.push({ type: 'weekly', label: 'Weekly' });
		if (date.getDate() === 1) {
			badges.push({ type: 'monthly', label: 'Monthly' });
			if ([0, 3, 6, 9].includes(date.getMonth())) {
				badges.push({ type: 'quarterly', label: 'Quarterly' });
			}
			if ([0, 6].includes(date.getMonth())) {
				badges.push({ type: 'semi', label: 'Semi-Annual' });
			}
			if (date.getMonth() === 0) {
				badges.push({ type: 'annual', label: 'Annual' });
			}
		}
		return badges;
	};

	return (
		<div className="space-y-8">
			<header className="rounded-2xl bg-white p-6 shadow-sm">
				<div className="flex flex-col gap-2">
					<h1 className="text-2xl font-semibold text-slate-900">InsightOS Compliance Operations Checklist</h1>
					<p className="text-sm text-slate-600">
						Capture proof (tickets, screenshots, exports) for each action and store evidence in
						<code className="ml-1 rounded bg-slate-100 px-2 py-0.5 text-xs">docs/security/evidence/&lt;YYYY-MM&gt;</code>.
					</p>
				</div>
				<div className="mt-4 grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
					<div>
						<label className="text-sm font-medium text-slate-700">Check date for submitted report</label>
						<input
							type="date"
							value={selectedDate}
							onChange={(e) => setSelectedDate(e.target.value)}
							className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
						/>
					</div>
					<button
						type="button"
						onClick={() => fetchReportForDate(selectedDate)}
						disabled={loadingReport}
						className="inline-flex items-center justify-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:opacity-50"
					>
						{loadingReport ? 'Checking…' : 'Load report'}
					</button>
				</div>
				{reportError && (
					<p className="mt-2 text-sm text-rose-600">{reportError}</p>
				)}
				{reportContent.length > 0 && (
					<div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
						<p className="text-sm font-semibold text-slate-800">Report content</p>
						<ul className="mt-2 space-y-2 text-sm text-slate-700">
							{reportContent.map((line, idx) => (
								<li key={`${line}-${idx}`} className="rounded bg-white px-3 py-2 shadow-sm">
									{line}
								</li>
							))}
						</ul>
					</div>
				)}
			</header>

			<section className="grid gap-6 lg:grid-cols-[2fr,1fr]">
				<div className="space-y-6">
					{checklistSections.map((section) => (
						<article key={section.key} className="rounded-2xl bg-white p-6 shadow-sm">
							<div className="flex flex-col gap-1">
								<h2 className="text-lg font-semibold text-slate-900">{section.title}</h2>
								<p className="text-sm text-slate-500">{section.description}</p>
							</div>
							<ul className="mt-4 space-y-3">
								{section.tasks.map((task, idx) => {
									const isChecked = completedMap[section.key]?.includes(idx) ?? false;
									return (
										<li key={task} className="flex items-start gap-3">
											<input
												id={`${section.key}-${idx}`}
												type="checkbox"
												className="mt-1 h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
												checked={isChecked}
												onChange={() => toggleTask(section.key, idx)}
											/>
											<label
												htmlFor={`${section.key}-${idx}`}
												className="text-sm text-slate-700"
											>
												{task}
											</label>
										</li>
									);
								})}
							</ul>
							<div className="mt-4">
								<label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
									Evidence / Notes
								</label>
								<textarea
									value={notes[section.key] || ''}
									onChange={(event) => handleNoteChange(section.key, event.target.value)}
									placeholder={section.notePlaceholder}
									className="mt-2 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-indigo-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200"
									rows={3}
								/>
							</div>
						</article>
					))}
				</div>

				<aside className="rounded-2xl bg-white p-6 shadow-sm">
					<div className="flex items-center justify-between">
						<button
							type="button"
							onClick={() => changeMonth(-1)}
							className="rounded-lg border border-slate-200 px-3 py-1 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
						>
							Prev
						</button>
						<div className="text-center">
							<p className="text-sm font-medium text-slate-500">Compliance Calendar</p>
							<p className="text-lg font-semibold text-slate-900">{format(currentMonth, 'MMMM yyyy')}</p>
						</div>
						<button
							type="button"
							onClick={() => changeMonth(1)}
							className="rounded-lg border border-slate-200 px-3 py-1 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
						>
							Next
						</button>
					</div>

					<div className="mt-4 grid grid-cols-7 gap-2 text-center text-xs font-semibold uppercase tracking-wide text-slate-400">
						{['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
							<div key={day}>{day}</div>
						))}
					</div>

					<div className="mt-2 grid grid-cols-7 gap-2 text-center text-sm">
						{monthMatrix.map((week, weekIdx) => (
							<React.Fragment key={`week-${weekIdx}`}>
								{week.map((date) => {
									const badges = getDayBadges(date);
									const isToday = isSameDay(date, new Date());
									const muted = !isSameMonth(date, currentMonth);
									return (
										<div
											key={date.toISOString()}
											className={`rounded-xl border p-2 text-xs ${
												muted
													? 'border-slate-100 bg-slate-50 text-slate-300'
													: 'border-slate-200 bg-slate-50 text-slate-700'
											} ${isToday ? 'ring-2 ring-indigo-400' : ''}`}
										>
											<div className="text-sm font-semibold">{format(date, 'd')}</div>
											<div className="mt-1 flex flex-col gap-1">
												{badges.map((badge) => (
													<span
														key={`${badge.type}-${format(date, 'yyyy-MM-dd')}-${badge.label}`}
														className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${calendarBadgeStyles[badge.type]}`}
													>
														{badge.label}
													</span>
												))}
											</div>
										</div>
									);
								})}
							</React.Fragment>
						))}
					</div>

					<div className="mt-4 space-y-2 text-xs text-slate-500">
						<p className="font-semibold text-slate-600">Legend</p>
						<div className="flex flex-wrap gap-2">
							{[
								{ label: 'Weekly (Mondays)', type: 'weekly' },
								{ label: 'Monthly (Day 1)', type: 'monthly' },
								{ label: 'Quarterly (Jan/Apr/Jul/Oct 1)', type: 'quarterly' },
								{ label: 'Semi-Annual (Jan/Jul 1)', type: 'semi' },
								{ label: 'Annual (Jan 1)', type: 'annual' },
							].map((item) => (
								<span
									key={item.type}
									className={`rounded-full px-3 py-1 font-semibold ${calendarBadgeStyles[item.type]}`}
								>
									{item.label}
								</span>
							))}
						</div>
					</div>
				</aside>
			</section>
		</div>
	);
}
